import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal, Overline } from "../components/Reveal";
import { formatPhone } from "../components/Header";
import { track, imgUrl } from "../lib/api";
import { Seo } from "../components/Seo";
import { SERVICE_SEO } from "../lib/seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { services, settings, L, lang } = useSite();
  const service = services.find((s) => s.slug === slug);
  const seo = SERVICE_SEO[slug];
  const content = seo ? seo.content[lang === "es" ? "es" : "en"] : null;

  if (services.length > 0 && !service) return <Navigate to="/services" replace />;
  if (!service) return <div className="min-h-screen" />;

  const others = services.filter((s) => s.slug !== slug);

  return (
    <main data-testid="service-detail-page">
      <Seo
        title={seo ? seo.title : `${service.title} in Huntsville, AL | Noyola Construction LLC`}
        description={seo ? seo.description : service.description}
        path={`/services/${slug}`}
      />
      <section className="relative bg-[#0B1B3D] pt-40 pb-24 overflow-hidden">
        <img src={`${imgUrl(service.image)}?w=1600&q=70`} alt={`${service.title} in Huntsville, AL — Noyola Construction`} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <Overline light>{L.serviceDetail.overline}</Overline>
            <h1 className="font-heading font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl">{service.title}</h1>
            <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">{service.description}</p>
          </Reveal>
        </div>
      </section>
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <img src={`${imgUrl(service.image)}?w=1200&q=75`} alt={`${service.title} project by Noyola Construction, Huntsville AL`} className="w-full h-[420px] object-cover border border-slate-200" />
              <div className="mt-10 space-y-4">
                {L.serviceDetail.features.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-[#005AE0] shrink-0" /> {f}
                  </div>
                ))}
              </div>
              {content && (
                <div className="mt-12" data-testid="service-seo-content">
                  <h2 className="font-heading font-bold text-[#0B1B3D] text-2xl mb-4">{content.heading}</h2>
                  <p className="text-slate-600 leading-relaxed mb-6">{content.intro}</p>
                  <ul className="space-y-3">
                    {content.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-[#005AE0] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500 leading-relaxed mt-6 text-sm">{content.area}</p>
                </div>
              )}
            </Reveal>
          </div>
          <div className="lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="bg-[#0B1B3D] p-10 sticky top-28">
                <h2 className="font-heading font-bold text-white text-2xl mb-4">{L.serviceDetail.boxTitle}</h2>
                <p className="text-slate-400 text-sm leading-relaxed mb-8">
                  {L.serviceDetail.boxText}
                </p>
                <Link to={`/estimate?service=${encodeURIComponent(service.title)}`} data-testid="service-detail-estimate-btn" className="btn-primary w-full mb-4">
                  {L.header.requestFreeEstimate}
                </Link>
                <a href={`tel:${settings.phone}`} data-testid="service-detail-call-btn" onClick={() => track("phone_click", { location: "service_detail" })} className="btn-outline w-full">
                  <Phone className="w-4 h-4" /> {formatPhone(settings.phone)}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      {others.length > 0 && (
        <section className="pb-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-heading font-bold text-[#0B1B3D] text-2xl mb-6">{lang === "es" ? "Otros servicios" : "Other Services"}</h2>
            <div className="flex flex-wrap gap-3" data-testid="related-services">
              {others.map((o) => (
                <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`related-service-${o.slug}`}
                  className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 text-sm text-[#0B1B3D] font-semibold hover:border-[#005AE0] hover:text-[#005AE0] transition-colors">
                  {o.title} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
              <Link to="/projects" data-testid="related-projects-link"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2.5 text-sm text-[#0B1B3D] font-semibold hover:border-[#005AE0] hover:text-[#005AE0] transition-colors">
                {lang === "es" ? "Ver proyectos" : "View Our Projects"} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
